import { supabase, withRetry } from './supabase';

export type LogLevel = 'info' | 'warning' | 'error' | 'debug';

export interface TaskLogEntry {
  task_id?: string;
  post_id?: string;
  level: LogLevel;
  event: string;
  details: Record<string, any>;
  timestamp: string;
}

class TaskLogger {
  private static instance: TaskLogger;
  private logQueue: TaskLogEntry[] = [];
  private isProcessing = false;
  private flushInterval: NodeJS.Timer | null = null;
  private consecutiveFailures = 0;
  private readonly MAX_CONSECUTIVE_FAILURES = 3;
  private readonly FAILURE_RESET_TIMEOUT = 60000; // 1 minute

  private constructor() {
    this.startFlushInterval();
  }

  public static getInstance(): TaskLogger {
    if (!TaskLogger.instance) {
      TaskLogger.instance = new TaskLogger();
    }
    return TaskLogger.instance;
  }

  private startFlushInterval() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flushInterval = setInterval(() => this.flushLogs(), 5000);
  }

  private async flushLogs() {
    if (this.isProcessing || this.logQueue.length === 0) return;

    // If we've had too many consecutive failures, wait before trying again
    if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
      console.warn(`Too many consecutive failures (${this.consecutiveFailures}), waiting before retrying...`);
      setTimeout(() => {
        this.consecutiveFailures = 0;
      }, this.FAILURE_RESET_TIMEOUT);
      return;
    }

    this.isProcessing = true;
    const logsToProcess = [...this.logQueue];
    this.logQueue = [];

    try {
      await withRetry(
        async () => {
          const { error } = await supabase
            .from('scheduled_tasks_log')
            .insert(logsToProcess.map(log => ({
              event_type: log.event,
              details: {
                ...log.details,
                level: log.level,
                task_id: log.task_id,
                post_id: log.post_id,
                timestamp: log.timestamp
              }
            })));

          if (error) {
            throw error;
          }

          // Reset consecutive failures on success
          this.consecutiveFailures = 0;
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          maxDelay: 5000,
          errorMessage: 'Failed to flush logs to Supabase',
          shouldRetry: (error) => {
            // Don't retry auth or permission errors
            if (error?.message?.includes('JWT') || error?.message?.includes('permission')) {
              return false;
            }
            return true;
          }
        }
      );
    } catch (error) {
      // Check if this is an auth error (401) — don't re-queue as retry won't help
      const isAuthError = error?.code === '401' || 
        error?.message?.includes('JWT') ||
        error?.message?.includes('401') ||
        error?.message?.includes('not authenticated') ||
        error?.status === 401;

      if (isAuthError) {
        // Silently discard — retrying with expired auth will never succeed
        console.debug('[TaskLogger] Auth expired, discarding', logsToProcess.length, 'log entries');
        this.consecutiveFailures = this.MAX_CONSECUTIVE_FAILURES; // Stop retrying
      } else {
        console.error('Error flushing logs:', error);
        // Put failed logs back in queue (only for non-auth errors)
        this.logQueue = [...logsToProcess, ...this.logQueue];
        this.consecutiveFailures++;

        // If queue is getting too large, remove oldest entries
        const MAX_QUEUE_SIZE = 1000;
        if (this.logQueue.length > MAX_QUEUE_SIZE) {
          console.warn(`Log queue exceeds ${MAX_QUEUE_SIZE} entries, removing oldest entries`);
          this.logQueue = this.logQueue.slice(-MAX_QUEUE_SIZE);
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  public async log(
    level: LogLevel,
    event: string,
    details: Record<string, any> = {},
    taskId?: string,
    postId?: string
  ) {
    const entry: TaskLogEntry = {
      task_id: taskId,
      post_id: postId,
      level,
      event,
      details,
      timestamp: new Date().toISOString()
    };

    // Add to queue
    this.logQueue.push(entry);

    // Also log to console for debugging
    const logPrefix = `[${level.toUpperCase()}] ${event}`;
    const logDetails = {
      taskId,
      postId,
      ...details,
      timestamp: entry.timestamp
    };

    switch (level) {
      case 'error':
        console.error(logPrefix, logDetails);
        break;
      case 'warning':
        console.warn(logPrefix, logDetails);
        break;
      case 'debug':
        console.debug(logPrefix, logDetails);
        break;
      default:
        console.log(logPrefix, logDetails);
    }

    // If queue is getting too large, flush immediately
    if (this.logQueue.length >= 50) {
      await this.flushLogs();
    }
  }

  public info(event: string, details?: Record<string, any>, taskId?: string, postId?: string) {
    return this.log('info', event, details, taskId, postId);
  }

  public warn(event: string, details?: Record<string, any>, taskId?: string, postId?: string) {
    return this.log('warning', event, details, taskId, postId);
  }

  public error(event: string, details?: Record<string, any>, taskId?: string, postId?: string) {
    return this.log('error', event, details, taskId, postId);
  }

  public debug(event: string, details?: Record<string, any>, taskId?: string, postId?: string) {
    return this.log('debug', event, details, taskId, postId);
  }

  public destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
  }
}

export const taskLogger = TaskLogger.getInstance();